/**
 * React hooks for event capture
 * Manages collector lifecycle and provides access to captured events
 */

import { useEffect, useCallback, useState } from 'react'
import { getSharedCollector } from '../util/event-capture/collector'

/**
 * Hook to install the event collector on mount and uninstall on unmount
 *
 * @param {Object} config - Optional configuration for the collector
 * @param {EventCollector} collector - Optional collector instance (defaults to shared singleton)
 */
export function useEventCollectorInstall(config, collector) {
  const collectorInstance = collector || getSharedCollector(config)

  useEffect(() => {
    collectorInstance.install()

    return () => {
      collectorInstance.uninstall()
    }
  }, [collectorInstance])
}

/**
 * Hook to subscribe to the event buffer
 * Note: React 17 compatible (uses useState instead of useSyncExternalStore)
 *
 * @param {EventCollector} collector - Optional collector instance (defaults to shared singleton)
 * @returns {Array} - Snapshot of current events
 */
export function useEventBuffer(collector) {
  const collectorInstance = collector || getSharedCollector()
  const [events, setEvents] = useState(() => collectorInstance.getSnapshot())

  useEffect(() => {
    // Update state when collector notifies of changes
    const unsubscribe = collectorInstance.subscribe(() => {
      setEvents(collectorInstance.getSnapshot())
    })

    // Sync with current state in case it changed before subscription
    setEvents(collectorInstance.getSnapshot())

    return unsubscribe
  }, [collectorInstance])

  return events
}

/**
 * Hook returning a stable callback for manually recording custom events
 *
 * @param {EventCollector} collector - Optional collector instance (defaults to shared singleton)
 * @returns {Function} - Callback to record custom events
 */
export function useRecordCustomEvent(collector) {
  const collectorInstance = collector || getSharedCollector()

  const recordEvent = useCallback(
    (eventType, data) => {
      collectorInstance.recordCustomEvent(eventType, data)
    },
    [collectorInstance]
  )

  return recordEvent
}

/**
 * Hook returning open/closed state for the report modal
 * Implemented as a separate channel from data subscriptions to avoid unnecessary re-renders
 * Note: React 17 compatible (uses useState instead of useSyncExternalStore)
 *
 * @param {EventCollector} collector - Optional collector instance (defaults to shared singleton)
 * @returns {Object} - { isOpen, openModal, closeModal }
 */
export function useReportModal(collector) {
  const collectorInstance = collector || getSharedCollector()
  const [modalState, setModalState] = useState(() => collectorInstance.getReportModalState())

  useEffect(() => {
    // Update state when modal state changes
    const unsubscribe = collectorInstance.subscribeToReportModal(() => {
      setModalState(collectorInstance.getReportModalState())
    })

    // Sync with current state in case it changed before subscription
    setModalState(collectorInstance.getReportModalState())

    return unsubscribe
  }, [collectorInstance])

  const openModal = useCallback(() => {
    collectorInstance.openReportModal()
  }, [collectorInstance])

  const closeModal = useCallback(() => {
    collectorInstance.closeReportModal()
  }, [collectorInstance])

  return { isOpen: modalState.isOpen, triggerMessage: modalState.triggerMessage, openModal, closeModal }
}

/**
 * Hook returning stable callback for clearing all events
 *
 * @param {EventCollector} collector - Optional collector instance (defaults to shared singleton)
 * @returns {Function} - Callback to clear all events from buffer and storage
 */
export function useClearEvents(collector) {
  const collectorInstance = collector || getSharedCollector()

  const clearEvents = useCallback(() => {
    collectorInstance.clearEvents()
  }, [collectorInstance])

  return clearEvents
}
